import type { Coverage, RelatedPerson } from '@medplum/fhirtypes';
import {
  COVERAGE_INSURANCE_TYPE_EXT,
  COVERAGE_INSURANCE_TYPE_CODES,
  type CoverageInsuranceTypeCode,
} from '@unify/fhir-model/constants';
import type { InsuranceEntry, InsurancePriority } from '@/components/billing/types';
import { buildIntakeCoverage, getCoverageRelationship, type CoverageRelationship } from '@unify/fhir-model/resources/coverage';
import { toCoverageUpsertCommand, type CoverageCommand } from '@unify/fhir-model/schemas';
import { formatCalendarDateUs, formatDateOnlyLocal, parseDateOnlyLocal } from '@/lib/dateHelpers';

export const INSURANCE_TYPE_LABELS: Record<CoverageInsuranceTypeCode, string> = {
  commercial: 'Commercial',
  medicare: 'Medicare',
  medicaid: 'Medicaid',
};

function formatEffectiveDate(value?: string): string | undefined {
  const dateOnly = value?.trim().slice(0, 10);
  if (!dateOnly) return undefined;
  const parsed = parseDateOnlyLocal(dateOnly);
  return parsed ? formatCalendarDateUs(parsed) : dateOnly;
}

/** Payer name plus effective dates when Coverage.period is present. */
export function coverageDropdownLabel(coverage: Coverage): string {
  const name = coverage.payor?.[0]?.display?.trim() || coverage.id || 'Coverage';
  const start = formatEffectiveDate(coverage.period?.start);
  const end = formatEffectiveDate(coverage.period?.end);
  if (!start && !end) return name;
  if (start && end) return `${name} (${start} – ${end})`;
  if (start) return `${name} (${start} –)`;
  return `${name} (– ${end})`;
}

export function getCoverageInsuranceType(coverage: Coverage): CoverageInsuranceTypeCode | undefined {
  const raw = coverage.extension?.find((e) => e.url === COVERAGE_INSURANCE_TYPE_EXT)?.valueCode;
  return COVERAGE_INSURANCE_TYPE_CODES.includes(raw as CoverageInsuranceTypeCode)
    ? (raw as CoverageInsuranceTypeCode)
    : undefined;
}

export function setCoverageInsuranceType(
  coverage: Coverage,
  insuranceType: CoverageInsuranceTypeCode,
): Coverage {
  const filtered = (coverage.extension ?? []).filter((e) => e.url !== COVERAGE_INSURANCE_TYPE_EXT);
  return {
    ...coverage,
    extension: [...filtered, { url: COVERAGE_INSURANCE_TYPE_EXT, valueCode: insuranceType }],
  };
}

function priorityFromOrder(order?: number): InsurancePriority {
  if (order === 2) return 'secondary';
  if (order === 3) return 'tertiary';
  return 'primary';
}

function orderFromPriority(priority: InsurancePriority): number {
  if (priority === 'secondary') return 2;
  if (priority === 'tertiary') return 3;
  return 1;
}

export interface InsuranceEntryWithType extends InsuranceEntry {
  insuranceType?: CoverageInsuranceTypeCode;
  planName?: string;
  periodStart?: string;
  periodEnd?: string;
  coverageStatus?: Coverage['status'];
}

const INACTIVE_COVERAGE_STATUSES = new Set<Coverage['status']>(['cancelled', 'entered-in-error']);

/** Coverages still shown in billing UI (excludes cancelled / entered-in-error). */
export function filterActiveCoverages(coverages: Coverage[]): Coverage[] {
  return coverages.filter((c) => !c.status || !INACTIVE_COVERAGE_STATUSES.has(c.status));
}

export function coverageToInsuranceEntryExtended(
  cov: Coverage,
  relatedPersons: RelatedPerson[] = [],
): InsuranceEntryWithType {
  const payor = cov.payor?.[0];
  const payerName = payor?.display ?? null;
  const payerOrgId = payor?.reference?.startsWith('Organization/')
    ? payor.reference.slice('Organization/'.length)
    : undefined;
  const memberId =
    cov.identifier?.find((id) => id.type?.coding?.[0]?.code === 'MB')?.value ??
    cov.subscriberId ??
    '';
  const groupId = cov.class?.find((c) => c.type?.coding?.[0]?.code === 'group')?.value ?? '';
  const planName = cov.class?.find((c) => c.type?.text === 'plan')?.name
    ?? cov.class?.find((c) => c.type?.text === 'plan')?.value;
  const subscriberRef = cov.subscriber?.reference ?? '';
  const isPatient = subscriberRef.startsWith('Patient/') || !subscriberRef;
  const rpId = subscriberRef.startsWith('RelatedPerson/')
    ? subscriberRef.slice('RelatedPerson/'.length)
    : undefined;
  const rp = rpId ? relatedPersons.find((r) => r.id === rpId) : undefined;
  const subscriberName = rp
    ? [rp.name?.[0]?.given?.[0] ?? '', rp.name?.[0]?.family ?? '']
    : !isPatient && cov.subscriber?.display
      ? cov.subscriber.display.split(' ')
      : [];
  const relationshipCode = getCoverageRelationship(cov);
  const subscriberRelationship =
    relationshipCode === 'spouse' || relationshipCode === 'child' || relationshipCode === 'other'
      ? relationshipCode
      : null;

  return {
    id: cov.id ?? `cov-${Math.random().toString(16).slice(2)}`,
    priority: priorityFromOrder(cov.order),
    provider: payerName,
    payerOrgId,
    coverageVersionId: cov.meta?.versionId,
    memberId,
    groupId,
    subscriberIsClient: isPatient,
    subscriberFirstName: subscriberName[0] ?? '',
    subscriberLastName: subscriberName.slice(1).join(' ') ?? '',
    subscriberDob: rp?.birthDate ? parseDateOnlyLocal(rp.birthDate) : null,
    subscriberRelationship: isPatient ? null : subscriberRelationship,
    verificationStatus: cov.status === 'active' ? 'verified' : 'pending',
    insuranceType: getCoverageInsuranceType(cov),
    planName,
    periodStart: cov.period?.start,
    periodEnd: cov.period?.end,
    coverageStatus: cov.status,
  };
}

export function staffCoverageCommandFromInsuranceEntry(
  patientId: string,
  entry: InsuranceEntryWithType,
  mode: 'add' | 'edit',
): CoverageCommand {
  const relationship: CoverageRelationship = entry.subscriberIsClient
    ? 'self'
    : entry.subscriberRelationship === 'spouse' || entry.subscriberRelationship === 'child'
      ? entry.subscriberRelationship
      : 'other';
  return toCoverageUpsertCommand({
    patientId,
    coverageId: mode === 'edit' ? entry.id : undefined,
    priority: entry.priority,
    payerOrgId: entry.payerOrgId ?? '',
    memberId: entry.memberId,
    groupNumber: entry.groupId,
    planName: entry.planName,
    relationship,
    subscriberFirstName: entry.subscriberFirstName,
    subscriberLastName: entry.subscriberLastName,
    subscriberDateOfBirth: entry.subscriberDob ? formatDateOnlyLocal(entry.subscriberDob) : undefined,
    periodStart: entry.periodStart,
    periodEnd: entry.periodEnd,
    insuranceType: COVERAGE_INSURANCE_TYPE_CODES.includes(entry.insuranceType as CoverageInsuranceTypeCode)
      ? (entry.insuranceType as CoverageInsuranceTypeCode)
      : undefined,
  });
}

export function insuranceEntryToCoverage(
  entry: InsuranceEntryWithType,
  patientRef: string,
  patientDisplay?: string,
  existing?: Coverage,
): Coverage {
  const relationship: CoverageRelationship = entry.subscriberIsClient ? 'self' : 'other';
  const built = buildIntakeCoverage({
    patientRef,
    patientDisplay,
    payerOrgId: existing?.payor?.[0]?.reference?.split('/')[1] ?? patientRef.split('/')[1] ?? 'unknown',
    payerName: entry.provider ?? 'Unknown payer',
    memberId: entry.memberId,
    groupId: entry.groupId || undefined,
    relationship,
    periodStart: entry.periodStart,
    periodEnd: entry.periodEnd,
  });
  let cov: Coverage = {
    ...built,
    id: existing?.id,
    meta: existing?.meta,
    status: entry.coverageStatus ?? existing?.status ?? 'active',
    order: orderFromPriority(entry.priority),
    payor: existing?.payor?.length
      ? existing.payor
      : [{ display: entry.provider ?? 'Unknown payer' }],
    class: [
      ...(entry.groupId
        ? [{ type: { coding: [{ code: 'group' }], text: 'group' }, value: entry.groupId }]
        : []),
      ...(entry.planName
        ? [{ type: { text: 'plan' }, value: entry.planName, name: entry.planName }]
        : []),
    ],
  };
  if (entry.insuranceType) {
    cov = setCoverageInsuranceType(cov, entry.insuranceType);
  }
  return cov;
}
